import { VariantProps, tva, useStyleContext, withStyleContext } from "@gluestack-ui/utils";
import { cssInterop } from "nativewind";
import React from "react";
import { Text, View } from "react-native";
import { IIconProps, Icon } from "../Icon/Icon";

const SCOPE = "ALERT";

cssInterop(View, { className: "style" });

const alertStyle = tva({
	base: "p-4 rounded-xl border gap-2 flex-row",
	variants: {
		action: {
			error: "bg-error-50",
			warning: "bg-warning-50",
			success: "bg-success-100",
			info: "bg-info-300",
			muted: "bg-neutral-50",
		},
		variant: {
			solid: "",
			outline: "bg-neutral-0",
		},
	},
	compoundVariants: [
		{
			variant: "solid",
			action: ["muted", "info"],
			className: "border-transparent",
		},
		{
			variant: "solid",
			action: "error",
			className: "border-transparent",
		},
		{
			variant: "solid",
			action: "success",
			className: "border-transparent",
		},
		{
			variant: "solid",
			action: "warning",
			className: "border-transparent",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "border-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "border-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "border-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "border-warning-700",
		},
	],
});

const alertTextStyle = tva({
	base: "text-neutral-950 font-sans font-normal text-md text-left flex-1",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		action: {
			error: "",
			warning: "",
			success: "",
			info: "",
			muted: "",
		},
	},
	parentCompoundVariants: [
		{
			variant: "solid",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "solid",
			action: "error",
			className: "text-error-800",
		},
		{
			variant: "solid",
			action: "warning",
			className: "text-warning-900",
		},
		{
			variant: "solid",
			action: "info",
			className: "text-info-950",
		},
		{
			variant: "solid",
			action: "muted",
			className: "text-neutral-700",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "text-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "text-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "text-warning-700",
		},
	],
});

const alertIconStyle = tva({
	base: "fill-none text-neutral-950 shrink-0",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		action: {
			error: "",
			warning: "",
			success: "",
			info: "",
			muted: "",
		},
	},
	parentCompoundVariants: [
		{
			variant: "solid",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "solid",
			action: "error",
			className: "text-error-800",
		},
		{
			variant: "solid",
			action: "warning",
			className: "text-warning-900",
		},
		{
			variant: "solid",
			action: "info",
			className: "text-info-950",
		},
		{
			variant: "solid",
			action: "muted",
			className: "text-neutral-700",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "text-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "text-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "text-warning-700",
		},
	],
});

const Root = withStyleContext(View, SCOPE);

export type IAlertProps = React.ComponentProps<typeof Root> & {
	className?: string;
} & VariantProps<typeof alertStyle>;

const Alert = React.forwardRef<React.ComponentRef<typeof Root>, IAlertProps>(function Alert(
	{ className, variant = "solid", action = "muted", ...props },
	ref,
) {
	return (
		<Root
			ref={ref}
			className={alertStyle({ variant, action, class: className })}
			context={{ variant, action }}
			{...props}
		/>
	);
});

type IAlertTextProps = React.ComponentProps<typeof Text> & {
	className?: string;
} & VariantProps<typeof alertTextStyle>;

const AlertText = React.forwardRef<React.ComponentRef<typeof Text>, IAlertTextProps>(function AlertText(
	{ className, ...props },
	ref,
) {
	const { variant: parentVariant, action: parentAction } = useStyleContext(SCOPE);
	return (
		<Text
			ref={ref}
			{...props}
			className={alertTextStyle({
				class: className,
				parentVariants: {
					variant: parentVariant,
					action: parentAction,
				},
			})}
		/>
	);
});

const AlertIcon = ({ className, ...props }: Omit<IIconProps, "size">) => {
	const { variant: parentVariant, action: parentAction } = useStyleContext(SCOPE);

	return (
		<Icon
			{...props}
			size="md"
			className={alertIconStyle({
				parentVariants: {
					variant: parentVariant,
					action: parentAction,
				},
				class: className,
			})}
		/>
	);
};

Alert.displayName = "Alert";
AlertText.displayName = "AlertText";
AlertIcon.displayName = "AlertIcon";

export { Alert, AlertText, AlertIcon };
